<template>
  <div class="resource-unsubscribe">
    <UkaskoLoader :is-loading="!isReady">
      <div class="resource-unsubscribe__top">
        <AppHeader
          class="resource-unsubscribe__header"
          :animation-enabled="false"
          :is-mobile-menu-open="isMobileMenuOpen"
          @header-resize="closeMobileMenuOnResize"
        />
        <section class="resource-unsubscribe__card">
          <h1 class="resource-unsubscribe__title">{{ text(`${state}-title`) }}</h1>
          <p class="resource-unsubscribe__text">{{ text(`${state}-text`) }}</p>
          <OrangeFancyButtonWithArrowAnimation
            v-if="state === 'confirm'"
            class="resource-unsubscribe__button"
            :disabled="isSubmitting"
            @click="onUnsubscribe"
          >
            {{ text('button') }}
          </OrangeFancyButtonWithArrowAnimation>
          <router-link v-else class="resource-unsubscribe__link" :to="{ name: 'resources' }">
            {{ text('back') }}
          </router-link>
        </section>
      </div>
      <div class="resource-unsubscribe__footer-edge"></div>
      <AppFooter />
    </UkaskoLoader>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { unsubscribeFromResources } from '../../api';
import { RESOURCE_UNSUBSCRIBE_TEXT_GROUP, RESOURCES_HUB_TEXT_PAGE } from '../../config';
import { textBlock } from '@/entities/text-block';
import { OrangeFancyButtonWithArrowAnimation } from '@/shared/ui/form';
import { UkaskoLoader } from '@/shared/ui/ukasko-loader';
import { AppFooter, AppHeader, promoMobileMenu } from '@/widgets/promo';

const PROMO_TEXT_PAGE = 'promo';

export default {
  props: {
    requestId: {
      type: String,
      required: true,
    },
    token: {
      type: String,
      required: true,
    },
  },
  mixins: [ promoMobileMenu, textBlock ],
  components: { AppHeader, AppFooter, UkaskoLoader, OrangeFancyButtonWithArrowAnimation },
  data() {
    return {
      state: this.token ? 'confirm' : 'error',
      isSubmitting: false,
    };
  },
  computed: {
    ...mapGetters('textBlocksModule', ['isTextBlocksReady']),
    isReady() {
      return this.isTextBlocksReady(PROMO_TEXT_PAGE) && this.isTextBlocksReady(RESOURCES_HUB_TEXT_PAGE);
    },
  },
  created() {
    if (!this.isTextBlocksReady(PROMO_TEXT_PAGE)) {
      this.fetchTextBlocks(PROMO_TEXT_PAGE);
    }
    if (!this.isTextBlocksReady(RESOURCES_HUB_TEXT_PAGE)) {
      this.fetchTextBlocks(RESOURCES_HUB_TEXT_PAGE);
    }
  },
  methods: {
    ...mapActions('textBlocksModule', ['fetchTextBlocks']),
    text(key) {
      return this.getKeyValueListBlockValue(RESOURCES_HUB_TEXT_PAGE, RESOURCE_UNSUBSCRIBE_TEXT_GROUP, key);
    },
    async onUnsubscribe() {
      this.isSubmitting = true;

      try {
        await unsubscribeFromResources(this.requestId, this.token);
        this.state = 'success';
      } catch (error) {
        this.state = 'error';
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.resource-unsubscribe {
  font-family: 'Geist', sans-serif;
  color: #373A3D;
  background-color: #FCFDFF;

  &__top {
    position: relative;
    min-height: 70vh;
    background-color: #CADDFC;
    padding-bottom: 96px;

    @media (max-width: 800px) {
      padding-bottom: 64px;
    }
  }

  &__header {
    position: relative;
    z-index: 2;
  }

  &__card {
    width: calc(100% - 48px);
    max-width: 640px;
    margin: 64px auto 0;
    padding: 40px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    border-radius: 28px;
    background-color: #FFFFFF;

    @media (max-width: 800px) {
      width: calc(100% - 32px);
      margin-top: 32px;
      padding: 24px 16px;
    }
  }

  &__title {
    margin: 0;
    font-family: 'IBM Plex Sans', sans-serif;
    font-weight: 500;
    font-size: clamp(28px, 3vw, 40px);
    line-height: 1.15;
  }

  &__text {
    margin: 0 0 8px;
    font-size: 16px;
    line-height: 1.5;
    color: #494E53;
  }

  &__button {
    width: auto;
    border-radius: 16px;
  }

  &__link {
    color: #4555CC;
    text-decoration: underline;

    &:hover {
      color: #373A3D;
    }
  }

  &__footer-edge {
    height: 32px;
    background-color: #313437;
    border-radius: 32px 32px 0 0;
  }
}
</style>
